import React, { useState } from 'react';
import SbEditable from 'storyblok-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Sentry from '@sentry/react';
import InputMask from 'react-input-mask';
import * as Yup from 'yup';
import styled from 'styled-components';
import queryString from 'query-string';

import { leads, metrics } from '../../services';
import TrackFormDirty from '../TrackFormDirty';
import { PhoneRegex } from '../../constants';
import './lead-form-referral.scss';

const defaultFormValues = {
  referralFirstName: '',
  referralLastName: '',
  referralPhone: '',
  referralEmail: '',
  firstName: '',
  lastName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  email: '',
  phone: '',
  notes: '',
};

const validationSchemaBase = {
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  zip: Yup.string().matches(/\d{5}/, 'Enter your ZIP code'),
  phone: Yup.string().matches(PhoneRegex, 'Enter a phone number'),
  email: Yup.string().email('Enter a valid e-mail address'),
};

const LeadFormReferral = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const onSubmit = async (values, actions) => {
    setSubmitState('');

    try {
      if (!values.referralPhone && !values.referralEmail) {
        setErrorMessage('Please enter either your phone number or e-mail address.');
        setSubmitState('failed');
        return;
      }

      if (!values.phone && !values.email) {
        setErrorMessage("Please enter either the referral's phone number or e-mail address.");
        setSubmitState('failed');
        return;
      }

      const leadSource = props.blok.lead_source || 'Online Media';
      const utmContent = props.blok.utm_content || 'Referral';
      const leadSourceMedium = props.blok.lead_source_medium || 'General Referral';
      const isProd =
        window.location.origin.indexOf('swellenergy.com') > -1 &&
        window.location.origin.indexOf('sbx') === -1;
      const defaultLeadSourceAccounts = props.blok.default_lead_source_account
        ? JSON.parse(props.blok.default_lead_source_account)
        : { dev: '0016100000fehkAAAQ', prod: '0014M00001k8A7fQAE' };
      const defaultLeadSourceAccount = isProd
        ? defaultLeadSourceAccounts.prod
        : defaultLeadSourceAccounts.dev;

      const parsed = queryString.parse(window.location.search);
      let owner = '';
      let queryPartnerId = '';

      if (parsed && parsed.partnerId) {
        queryPartnerId = parsed.partnerId;
        if (queryPartnerId.includes('?')) {
          queryPartnerId = queryPartnerId.substring(0, queryPartnerId.indexOf('?'));
        }
      }

      if (queryPartnerId && queryPartnerId.length > 0) {
        owner = queryPartnerId;
      }

      let promotion = null;

      if (props.blok.promotion) {
        const promotions = JSON.parse(props.blok.promotion);
        promotion = isProd ? promotions.prod : promotions.dev;
      }

      await leads.submitLead({
        ...values,
        leadSource,
        leadSourceAccount: !!parsed
          ? queryPartnerId || defaultLeadSourceAccount
          : defaultLeadSourceAccount,
        leadSourceMedium,
        utmContent,
        owner,
        promotion,
      });

      setSubmitState('success');
      metrics.trackLeadFormSubmit(
        window.location.origin + window.location.pathname,
        'LeadFormReferral'
      );

      const newFormValues = {
        ...defaultFormValues,
        referralFirstName: values.referralFirstName,
        referralLastName: values.referralLastName,
        referralEmail: values.referralEmail,
        referralPhone: values.referralPhone,
      };

      actions.setValues(newFormValues);
      actions.setTouched({});
    } catch (e) {
      Sentry.captureException(e, {
        lead: values,
      });
      setErrorMessage('We had a problem processing your request. Please try again.');
      setSubmitState('failed');
    }
  };

  const containerClasses = ['lfr'];
  containerClasses.push('theme-' + props.blok.theme);

  const title = props.blok.title || '';
  const submitButtonText = props.blok.submit_button_text || 'Submit';
  const submittingButtonText = props.blok.submitting_button_text || 'Submitting...';
  const buttonStyle = props.blok.button_style || 'blue';
  const successMessage = props.blok.success_message || 'Thank you for your referral!';
  const referrerType = props.blok.referrer_type || 'individual';
  const isIndividual = referrerType === 'individual';

  var validationSchema;

  if (referrerType === 'individual') {
    validationSchema = Yup.object().shape({
      ...validationSchemaBase,
      referralFirstName: Yup.string().required('Required'),
      referralLastName: Yup.string().required('Required'),
      referralPhone: Yup.string().matches(PhoneRegex, 'Enter your phone number'),
      referralEmail: Yup.string().email('Enter a valid e-mail address'),
    });
  } else {
    validationSchema = Yup.object().shape({
      ...validationSchemaBase,
      referralLastName: Yup.string().required('Required'),
      referralPhone: Yup.string().matches(PhoneRegex, 'Enter your company phone number'),
      referralEmail: Yup.string().email('Enter a valid company e-mail address'),
    });
  }

  return (
    <SbEditable content={props.blok}>
      <div className={containerClasses.join(' ')}>
        {
          <Formik
            initialValues={defaultFormValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            {(formik) => {
              const buttonText = formik.isSubmitting ? submittingButtonText : submitButtonText;

              return (
                <Form className="input-form">
                  {title && <h2>{title}</h2>}
                  <TrackFormDirty
                    formComponent="LeadFormReferral"
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                  />
                  {isIndividual && (
                    <>
                      <h4>Your Name</h4>
                      <div className="fields">
                        <div className="field">
                          <Field name="referralFirstName" placeholder="First Name" />
                          <ErrorMessage
                            className="error"
                            component="div"
                            name="referralFirstName"
                          />
                        </div>
                        <div className="field">
                          <Field name="referralLastName" placeholder="Last Name" />
                          <ErrorMessage className="error" component="div" name="referralLastName" />
                        </div>
                      </div>
                      <div className="fields">
                        <div className="field">
                          <Field name="referralPhone" placeholder="Phone">
                            {({ field }) => (
                              <InputMask
                                {...field}
                                mask="(999) 999-9999"
                                placeholder="Phone"
                                type="text"
                                alwaysShowMask={false}
                              />
                            )}
                          </Field>
                          <ErrorMessage className="error" component="div" name="referralPhone" />
                        </div>
                        <div className="field">
                          <Field name="referralEmail" type="email" placeholder="Email" />
                          <ErrorMessage className="error" component="div" name="referralEmail" />
                        </div>
                      </div>
                    </>
                  )}
                  {!isIndividual && (
                    <>
                      <h4>Your Company</h4>
                      <div className="field">
                        <Field name="referralLastName" placeholder="Company Name" />
                        <ErrorMessage className="error" component="div" name="referralLastName" />
                      </div>
                      <div className="fields">
                        <div className="field">
                          <Field name="referralPhone" placeholder="Company Phone">
                            {({ field }) => (
                              <InputMask
                                {...field}
                                mask="(999) 999-9999"
                                placeholder="Company Phone"
                                type="text"
                                alwaysShowMask={false}
                              />
                            )}
                          </Field>
                          <ErrorMessage className="error" component="div" name="referralPhone" />
                        </div>
                        <div className="field">
                          <Field name="referralEmail" type="email" placeholder="Company Email" />
                          <ErrorMessage className="error" component="div" name="referralEmail" />
                        </div>
                      </div>
                    </>
                  )}
                  <h4>Who would you like to refer?</h4>
                  <div className="fields">
                    <div className="field">
                      <Field name="firstName" placeholder="First Name" />
                      <ErrorMessage className="error" component="div" name="firstName" />
                    </div>
                    <div className="field">
                      <Field name="lastName" placeholder="Last Name" />
                      <ErrorMessage className="error" component="div" name="lastName" />
                    </div>
                  </div>
                  <div className="fields">
                    <div className="field">
                      <Field name="phone" placeholder="Phone">
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="(999) 999-9999"
                            placeholder="Phone"
                            type="text"
                            alwaysShowMask={false}
                          />
                        )}
                      </Field>
                      <ErrorMessage className="error" component="div" name="phone" />
                    </div>
                    <div className="field">
                      <Field name="email" placeholder="Email" />
                      <ErrorMessage className="error" component="div" type="email" name="email" />
                    </div>
                  </div>
                  <div className="fields">
                    <div className="field">
                      <Field name="address" placeholder="Street Address" />
                      <ErrorMessage className="error" component="div" name="address" />
                    </div>
                    <div className="field">
                      <Field name="city" placeholder="City" />
                      <ErrorMessage className="error" component="div" name="city" />
                    </div>
                  </div>
                  <div className="fields">
                    <div className="field">
                      <Field name="zip" placeholder="Zip Code">
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="99999"
                            placeholder="Zip Code"
                            type="text"
                            alwaysShowMask={false}
                          />
                        )}
                      </Field>
                      <ErrorMessage className="error" component="div" name="zip" />
                    </div>
                    <div className="field">
                      <Field name="state" placeholder="State" />
                      <ErrorMessage className="error" component="div" name="state" />
                    </div>
                  </div>
                  <div className="field">
                    <Field
                      name="notes"
                      as="textarea"
                      rows="6"
                      placeholder="Additional referral / project information? (optional)"
                    />
                  </div>
                  {submitState === 'failed' && <div className="error">{errorMessage}</div>}
                  {submitState === 'success' && <div className="success">{successMessage}</div>}
                  <button
                    className={`btn-${buttonStyle}`}
                    disabled={formik.isSubmitting}
                    type="submit"
                  >
                    {buttonText}
                  </button>
                  {props.blok.submit_disclaimer && (
                    <Disclaimer>{props.blok.submit_disclaimer}</Disclaimer>
                  )}
                </Form>
              );
            }}
          </Formik>
        }
      </div>
    </SbEditable>
  );
};

const Disclaimer = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  display: block;
`;

export default LeadFormReferral;
